import { useState } from 'react'
import {
  Box,
  Button,
  DialogContentText,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  refusalDetail,
  usePolicyConfigurationBenefitInfoAction,
  type BenefitSection,
  type PolicyBenefitInfo,
  type PolicyBenefitInfoAction,
  type PolicyConfigurationDetail,
} from './BenefitPolicyService'
import { NumberedSectionCard } from '~/shared/ui'
import { BenefitInfoDialog } from './BenefitInfoDialog'
import { RecordDialog } from './RecordDialog'

interface BenefitInformationSectionProps {
  policyId: number
  benefitInformation: PolicyConfigurationDetail['benefit_information']
  documents: PolicyConfigurationDetail['documents']
  isActive: boolean
  readOnly: boolean
}

/** In the order the editor stacks them. Keyed by the endpoint's own section names. */
const SECTIONS = [
  { key: 'pharmacy', label: 'Pharmacy' },
  { key: 'dental', label: 'Dental' },
  { key: 'vision', label: 'Vision' },
] as const satisfies readonly { key: BenefitSection; label: string }[]

/** Which dialog is up, and on which row. An add carries the section it lands in. */
type Target =
  | { kind: 'add'; section: BenefitSection; label: string }
  | { kind: 'edit'; section: BenefitSection; label: string; benefitInfo: PolicyBenefitInfo }
  | { kind: 'remove'; benefitInfo: PolicyBenefitInfo }

const BenefitInfoRow = ({
  benefitInfo,
  documents,
  readOnly,
  onEdit,
  onRemove,
}: {
  benefitInfo: PolicyBenefitInfo
  documents: PolicyConfigurationDetail['documents']
  readOnly: boolean
  onEdit: () => void
  onRemove: () => void
}) => {
  // The document's name and address live on `documents`, which this screen already loaded for the
  // picker — the row names the document rather than repeating them.
  const document = documents.find(candidate => candidate.id === benefitInfo.document)

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: 'flex-start',
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
      }}
      data-testid={`benefit-info-${benefitInfo.id}`}
    >
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', width: 24, flex: 'none', mt: 0.5 }}
      >
        {benefitInfo.display_order}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {benefitInfo.title || <em>Untitled</em>}
        </Typography>
        {benefitInfo.summary && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {benefitInfo.summary}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
          {benefitInfo.has_call_to_action ? `${benefitInfo.link_label} → ` : 'No link · '}
          {document ? (
            document.name
          ) : benefitInfo.url ? (
            <Link href={benefitInfo.url} target="_blank" rel="noopener" underline="hover">
              {benefitInfo.url}
            </Link>
          ) : (
            'nothing to open'
          )}
        </Typography>
      </Box>
      {!readOnly && (
        <Stack direction="row" spacing={0.5} sx={{ flex: 'none' }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={onEdit}
              data-testid={`benefit-info-${benefitInfo.id}-edit`}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton
              size="small"
              onClick={onRemove}
              data-testid={`benefit-info-${benefitInfo.id}-remove`}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  )
}

/**
 * Section 5: the copy a member reads on the Pharmacy and Dental & vision screens.
 *
 * Written a row at a time through its own endpoint, like the plan documents above it — these are
 * records rather than form fields, so they commit as they are authored rather than waiting for the
 * form's Save.
 */
export const BenefitInformationSection = ({
  policyId,
  benefitInformation,
  documents,
  isActive,
  readOnly,
}: BenefitInformationSectionProps) => {
  const [target, setTarget] = useState<Target | null>(null)
  const benefitInfoAction = usePolicyConfigurationBenefitInfoAction(policyId)

  const refusal = benefitInfoAction.isError
    ? refusalDetail(benefitInfoAction.error, 'Could not update the benefit information.')
    : undefined

  // Reset first: a refusal answers one attempt, and carrying it into the next dialog reads as a
  // fresh failure before anything has been sent.
  const openDialog = (next: Target) => {
    benefitInfoAction.reset()
    setTarget(next)
  }

  // Closes only on success: a refusal leaves the dialog open with the server's reason on it.
  const act = (action: PolicyBenefitInfoAction) =>
    benefitInfoAction.mutate(action, { onSuccess: () => setTarget(null) })

  return (
    <NumberedSectionCard
      index={5}
      title="Benefit information"
      hint="What members read on the Pharmacy and Dental & vision screens, lowest order first."
      testId="section-benefit-information"
    >
      <Stack spacing={3}>
        {SECTIONS.map(({ key, label }) => {
          const benefitInfos = benefitInformation[key]
          return (
            <Stack key={key} spacing={1} sx={{ alignItems: 'flex-start' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {label}
              </Typography>

              {benefitInfos.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Nothing on the {label.toLowerCase()} section yet.
                </Typography>
              ) : (
                <Box sx={{ width: '100%' }}>
                  {benefitInfos.map(benefitInfo => (
                    <BenefitInfoRow
                      key={benefitInfo.id}
                      benefitInfo={benefitInfo}
                      documents={documents}
                      readOnly={readOnly}
                      onEdit={() => openDialog({ kind: 'edit', section: key, label, benefitInfo })}
                      onRemove={() => openDialog({ kind: 'remove', benefitInfo })}
                    />
                  ))}
                </Box>
              )}

              {!readOnly && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openDialog({ kind: 'add', section: key, label })}
                  data-testid={`add-benefit-${key}`}
                >
                  Add {label.toLowerCase()} info
                </Button>
              )}
            </Stack>
          )
        })}
      </Stack>

      {(target?.kind === 'add' || target?.kind === 'edit') && (
        <BenefitInfoDialog
          benefitInfo={target.kind === 'edit' ? target.benefitInfo : undefined}
          section={target.section}
          sectionLabel={target.label}
          documents={documents}
          isActive={isActive}
          saving={benefitInfoAction.isPending}
          refusal={refusal}
          onClose={() => setTarget(null)}
          onConfirm={act}
        />
      )}
      {target?.kind === 'remove' && (
        <RecordDialog
          title={`Remove “${
            target.benefitInfo.title || target.benefitInfo.summary || 'this benefit info'
          }”?`}
          confirmLabel="Remove"
          destructive
          busy={benefitInfoAction.isPending}
          notice={
            isActive
              ? 'This policy is live. This comes off the member’s screen as soon as you remove it.'
              : undefined
          }
          refusal={refusal}
          testId="remove-benefit-info"
          maxWidth="xs"
          onClose={() => setTarget(null)}
          onConfirm={() => act({ action: 'remove', benefit_info: target.benefitInfo.id })}
        >
          <DialogContentText>
            It comes off this policy and stops reaching members on that screen.
          </DialogContentText>
        </RecordDialog>
      )}
    </NumberedSectionCard>
  )
}
